import { SEM_ORIGEM, valoresDoGerenciador, type FiltroDeCampo, type OpcaoDeFiltro } from "@base/contracts";
import { Button, Popover, PopoverContent, PopoverTrigger, cn } from "@base/ui";
import { Check, ListFilter, Search, X } from "lucide-react";
import { useState } from "react";
import { formatar } from "../domain/formatadores";

const normalizar = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

type Rascunho = ReadonlyMap<number, ReadonlySet<string>>;

const paraRascunho = (filtros: readonly FiltroDeCampo[]): Rascunho =>
  new Map(filtros.map((filtro) => [filtro.campoId, new Set(filtro.valores)]));

const paraFiltros = (rascunho: Rascunho): FiltroDeCampo[] =>
  [...rascunho]
    .filter(([, valores]) => valores.size > 0)
    .map(([campoId, valores]) => ({ campoId, valores: [...valores] }));

/**
 * Botao "Filtros" do Dashboard: abre um popup com os campos personalizados do
 * Kommo (Origem, Sub Origem...) e os valores que existem nos leads. Dentro de
 * um campo vale qualquer valor marcado; entre campos, todos ao mesmo tempo.
 * As escolhas so valem ao clicar em "Aplicar".
 */
export function FiltrosDoDashboard({
  campos,
  carregando,
  valor,
  aoMudar,
}: {
  readonly campos: readonly OpcaoDeFiltro[];
  readonly carregando: boolean;
  readonly valor: readonly FiltroDeCampo[];
  readonly aoMudar: (filtros: FiltroDeCampo[]) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [rascunho, setRascunho] = useState<Rascunho>(() => paraRascunho(valor));
  const ativos = valor.reduce((total, filtro) => total + filtro.valores.length, 0);
  const marcados = [...rascunho.values()].reduce((total, valores) => total + valores.size, 0);

  function abrir(proximo: boolean) {
    if (proximo) setRascunho(paraRascunho(valor));
    setAberto(proximo);
  }

  function marcarGerenciador() {
    const origem = campos.find((campo) => normalizar(campo.nome) === "origem");
    if (!origem) return;
    setRascunho((atual) => {
      const proximo = new Map(atual);
      proximo.set(origem.campoId, new Set(valoresDoGerenciador(origem.opcoes.map((opcao) => opcao.valor))));
      return proximo;
    });
  }

  function alternar(campoId: number, opcao: string) {
    setRascunho((atual) => {
      const proximo = new Map(atual);
      const valores = new Set(proximo.get(campoId) ?? []);
      if (valores.has(opcao)) valores.delete(opcao);
      else valores.add(opcao);
      proximo.set(campoId, valores);
      return proximo;
    });
  }

  return (
    <Popover open={aberto} onOpenChange={abrir}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-11 gap-2 rounded-full px-4">
          <ListFilter aria-hidden="true" />
          Filtros
          {ativos > 0 ? (
            <span className="grid min-w-5 place-items-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground tabular-nums">
              {ativos}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex max-h-[min(40rem,80svh)] w-[min(30rem,calc(100vw-2rem))] flex-col overflow-hidden p-0">
        <div className="flex items-center justify-between gap-3 border-b px-5 py-4">
          <div className="flex flex-col">
            <h2 className="text-base font-semibold">Filtros</h2>
            <p className="text-xs text-muted-foreground">Campos personalizados do Kommo</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            disabled={marcados === 0}
            onClick={() => setRascunho(new Map())}
          >
            Limpar
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-x-hidden overflow-y-auto bg-muted/30 px-4 py-4">
          {carregando ? (
            <p className="text-sm text-muted-foreground">Carregando os campos…</p>
          ) : campos.length === 0 ? (
            <p className="text-sm leading-relaxed text-muted-foreground">
              Nenhum campo de filtro ainda. A equipe Monte ou o gestor escolhem os campos em Configurações ›
              Conexões.
            </p>
          ) : (
            <>
              <AtalhoDoGerenciador campos={campos} aoMarcar={marcarGerenciador} />
              {campos.map((campo) => (
              <CampoDoFiltro
                key={campo.campoId}
                campo={comSemOrigem(campo)}
                marcados={rascunho.get(campo.campoId) ?? new Set()}
                aoAlternar={(opcao) => alternar(campo.campoId, opcao)}
                aoLimpar={() =>
                  setRascunho((atual) => {
                    const proximo = new Map(atual);
                    proximo.delete(campo.campoId);
                    return proximo;
                  })
                }
              />
              ))}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t bg-muted/30 px-5 py-3">
          <span className="text-xs text-muted-foreground">
            {marcados === 0 ? "Todos os leads" : `${marcados} ${marcados === 1 ? "valor marcado" : "valores marcados"}`}
          </span>
          <Button
            onClick={() => {
              aoMudar(paraFiltros(rascunho));
              setAberto(false);
            }}
          >
            Aplicar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function comSemOrigem(campo: OpcaoDeFiltro): OpcaoDeFiltro {
  if (normalizar(campo.nome) !== "origem" || campo.opcoes.some((opcao) => opcao.valor === SEM_ORIGEM)) return campo;
  return { ...campo, opcoes: [...campo.opcoes, { valor: SEM_ORIGEM, quantidade: 0 }] };
}

/** Atalho: marca Meta, Google, organico, fonte nao identificada e origem vazia. */
function AtalhoDoGerenciador({
  campos,
  aoMarcar,
}: {
  readonly campos: readonly OpcaoDeFiltro[];
  readonly aoMarcar: () => void;
}) {
  if (!campos.some((campo) => normalizar(campo.nome) === "origem")) return null;
  return (
    <button
      type="button"
      onClick={aoMarcar}
      className="flex flex-col items-start gap-0.5 rounded-2xl border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-accent"
    >
      <span className="text-sm font-semibold">Gerenciador</span>
      <span className="text-xs text-muted-foreground">Meta, Google e orgânico</span>
    </button>
  );
}

function CampoDoFiltro({
  campo,
  marcados,
  aoAlternar,
  aoLimpar,
}: {
  readonly campo: OpcaoDeFiltro;
  readonly marcados: ReadonlySet<string>;
  readonly aoAlternar: (opcao: string) => void;
  readonly aoLimpar: () => void;
}) {
  const [busca, setBusca] = useState("");
  const termo = normalizar(busca);
  const visiveis = campo.opcoes.filter((opcao) => termo === "" || normalizar(opcao.valor).includes(termo));

  return (
    <fieldset className="min-w-0 shrink-0 overflow-hidden rounded-2xl border border-border bg-card">
      <legend className="sr-only">{campo.nome}</legend>
      <div className="flex items-center justify-between gap-3 px-4 pt-3 pb-2">
        <span className="truncate text-sm font-semibold" aria-hidden="true">
          {campo.nome}
        </span>
        {marcados.size > 0 ? (
          <button
            type="button"
            onClick={aoLimpar}
            className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            {marcados.size} marcado{marcados.size === 1 ? "" : "s"} · limpar
          </button>
        ) : (
          <span className="text-xs text-muted-foreground">
            {campo.opcoes.length === 0 ? "Sem dados" : `${campo.opcoes.length} ${campo.opcoes.length === 1 ? "opção" : "opções"}`}
          </span>
        )}
      </div>

      {campo.opcoes.length === 0 ? (
        <p className="px-4 pb-3 text-xs text-muted-foreground">Nenhum lead com este campo preenchido no Kommo.</p>
      ) : (
        <>
          <label className="mx-3 mb-2 flex h-9 items-center gap-2 rounded-lg border border-input bg-background px-3">
            <Search className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            <input
              value={busca}
              onChange={(evento) => setBusca(evento.target.value)}
              placeholder={`Buscar em ${campo.nome}`}
              aria-label={`Buscar em ${campo.nome}`}
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </label>
          {/* Altura fixa com rolagem: lista longa nao empurra os outros campos. */}
          <ul className="max-h-44 overflow-x-hidden overflow-y-auto border-t px-1.5 py-1.5" aria-label={`Opções de ${campo.nome}`}>
            {visiveis.map((opcao) => {
              const marcado = marcados.has(opcao.valor);
              return (
                <li key={opcao.valor}>
                  <label
                    title={opcao.valor}
                    className={cn(
                      "flex min-w-0 cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 text-sm transition-colors hover:bg-accent",
                      marcado && "bg-accent/60",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={marcado}
                      onChange={() => aoAlternar(opcao.valor)}
                      className="peer sr-only"
                    />
                    <span
                      aria-hidden="true"
                      className={cn(
                        "grid size-4 shrink-0 place-items-center rounded border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40",
                        marcado ? "border-primary bg-primary text-primary-foreground" : "border-input bg-background",
                      )}
                    >
                      {marcado ? <Check className="size-3" /> : null}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{opcao.valor}</span>
                    {opcao.valor === SEM_ORIGEM ? null : (
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {formatar.quantidade(opcao.quantidade)}
                      </span>
                    )}
                  </label>
                </li>
              );
            })}
            {visiveis.length === 0 ? (
              <li className="px-2.5 py-3 text-center text-xs text-muted-foreground">Nada encontrado.</li>
            ) : null}
          </ul>
        </>
      )}
    </fieldset>
  );
}

/** Os filtros aplicados, em chips removiveis, logo abaixo do cabecalho. */
export function FiltrosAtivos({
  campos,
  valor,
  aoMudar,
}: {
  readonly campos: readonly OpcaoDeFiltro[];
  readonly valor: readonly FiltroDeCampo[];
  readonly aoMudar: (filtros: FiltroDeCampo[]) => void;
}) {
  if (valor.length === 0) return null;
  const nomeDo = (campoId: number) => campos.find((campo) => campo.campoId === campoId)?.nome ?? "Campo";

  function remover(campoId: number, opcao: string) {
    aoMudar(
      valor
        .map((filtro) =>
          filtro.campoId === campoId ? { ...filtro, valores: filtro.valores.filter((item) => item !== opcao) } : filtro,
        )
        .filter((filtro) => filtro.valores.length > 0),
    );
  }

  return (
    <div className="-mt-6 flex flex-wrap items-center gap-2" aria-label="Filtros aplicados">
      {valor.flatMap((filtro) =>
        filtro.valores.map((opcao) => (
          <span
            key={`${filtro.campoId}:${opcao}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-accent py-1 pr-1 pl-3 text-sm text-accent-foreground"
          >
            <span className="text-xs opacity-75">{nomeDo(filtro.campoId)}:</span>
            {opcao}
            <button
              type="button"
              onClick={() => remover(filtro.campoId, opcao)}
              aria-label={`Remover filtro ${nomeDo(filtro.campoId)}: ${opcao}`}
              className="grid size-5 place-items-center rounded-full hover:bg-black/10"
            >
              <X className="size-3" aria-hidden="true" />
            </button>
          </span>
        )),
      )}
      <Button variant="link" size="sm" onClick={() => aoMudar([])}>
        Limpar filtros
      </Button>
    </div>
  );
}
